<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import { ElTimePicker } from 'element-plus'

type TimePickerValue = string | string[]

defineOptions({
  name: 'AppTimePicker',
})

const props = withDefaults(defineProps<{
  modelValue?: TimePickerValue | null
  valueFormat?: 'HH:mm' | 'HH:mm:ss' | string
  format?: string
  placeholder?: string
  startPlaceholder?: string
  endPlaceholder?: string
  rangeSeparator?: string
  clearable?: boolean
  disabled?: boolean
  readonly?: boolean
  isRange?: boolean
  size?: '' | 'large' | 'default' | 'small'
  class?: HTMLAttributes['class']
}>(), {
  modelValue: '',
  valueFormat: 'HH:mm:ss',
  format: '',
  placeholder: '选择时间',
  startPlaceholder: '开始时间',
  endPlaceholder: '结束时间',
  rangeSeparator: '至',
  clearable: true,
  disabled: false,
  readonly: false,
  isRange: false,
  size: 'default',
  class: undefined,
})

const emit = defineEmits<{
  'update:modelValue': [value: TimePickerValue]
  'change': [value: TimePickerValue]
  'clear': []
}>()

const effectiveFormat = computed(() => props.format || props.valueFormat)

function normalizeSingleValue(value: unknown) {
  if (!value) {
    return ''
  }
  const normalized = String(value)
  if (props.valueFormat.includes('ss') && /^\d{2}:\d{2}$/.test(normalized)) {
    return `${normalized}:00`
  }
  return props.valueFormat.includes('ss') ? normalized : normalized.slice(0, 5)
}

function normalizeValue(value: unknown): TimePickerValue {
  if (Array.isArray(value)) {
    return value.map(normalizeSingleValue)
  }
  return normalizeSingleValue(value)
}

function handleUpdate(value: unknown) {
  emit('update:modelValue', normalizeValue(value))
}

function handleChange(value: unknown) {
  emit('change', normalizeValue(value))
}

function handleClear() {
  emit('clear')
}
</script>

<template>
  <ElTimePicker
    :model-value="props.modelValue"
    :is-range="props.isRange"
    :value-format="props.valueFormat"
    :format="effectiveFormat"
    :placeholder="props.placeholder"
    :start-placeholder="props.startPlaceholder"
    :end-placeholder="props.endPlaceholder"
    :range-separator="props.rangeSeparator"
    :clearable="props.clearable"
    :disabled="props.disabled"
    :readonly="props.readonly"
    :size="props.size || undefined"
    class="w-full"
    :class="props.class"
    @update:model-value="handleUpdate"
    @change="handleChange"
    @clear="handleClear"
  />
</template>
